"use client";

import {
  TypeFaq,
  TypeQuestionAndAnswer,
  isTypeQuestionAndAnswer,
} from "@contentful/types";
import { isEntry } from "@contentful/utils";
import { faPhone } from "@fortawesome/pro-light-svg-icons/faPhone";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import * as Accordion from "@radix-ui/react-accordion";
// eslint-disable-next-line no-restricted-imports
import { EnvelopeClosedIcon } from "@radix-ui/react-icons";
import { ChevronDownIcon } from "@radix-ui/themes";
import { H2 } from "@yardzen/ui/components/h2";
import { Link } from "@yardzen/ui/components/link";
import { cn } from "@yardzen/ui/lib/utils";
import { cva } from "class-variance-authority";
import classNames from "classnames";
import React, { ReactNode } from "react";
import { FaqJsonLd } from "./FaqJsonLd";
import { RichText } from "./rich-text/RichText";
import { H1 } from "@yardzen/ui/components/h1";

export const FAQ = ({
  faq,
}: {
  faq: TypeFaq<"WITHOUT_UNRESOLVABLE_LINKS", string>;
}) => {
  const questionsAndAnswers = faq.fields.questionsAndAnswers?.reduce<
    TypeQuestionAndAnswer<"WITHOUT_UNRESOLVABLE_LINKS", string>[]
  >((acc, question) => {
    if (!isEntry(question) || !isTypeQuestionAndAnswer(question)) {
      return acc;
    }
    return [...acc, question];
  }, []);

  const normalizedVariant = faq.fields.variant ?? "default";
  const faqVariants = cva("", {
    variants: {
      accordianContent: {
        "title-top": "px-0 pt-0 pb-6 md:pr-16",
        default: "px-0 pt-0 pb-6 md:pr-16",
      },
      accordianItem: {
        "title-top": "first:border-t border-b border-black/10",
        default: "first:border-t border-b border-black/10",
      },
      triggerText: {
        "title-top":
          "px-0 py-6 text-base md:text-[18px] font-bold leading-[1.4] tracking-[-0.01em]",
        default:
          "px-0 py-6 text-base md:text-[18px] font-bold leading-[1.4] tracking-[-0.01em]",
      },
      triggerChevron: {
        "title-top":
          "min-w-[16px] w-[16px] h-[16px] ml-4 text-neutral-400 group-data-[state=open]:text-neutral-600",
        default:
          "min-w-[16px] w-[16px] h-[16px] ml-4 text-neutral-400 group-data-[state=open]:text-neutral-600",
      },
      pSpacing: {
        "title-top": "false",
        default: "false",
      },
      pText: {
        "title-top": "text-base leading-relaxed text-neutral-700 mb-0",
        default: "text-base leading-relaxed text-neutral-700 mb-0",
      },
    },
  });

  function FaqAccordian() {
    return (
      <Accordion.Root className="border-black-elderberry" type="multiple">
        {questionsAndAnswers?.map((questionsAndAnswer) => (
          <AccordionItem
            key={questionsAndAnswer.sys.id}
            value={questionsAndAnswer.sys.id}
            className={faqVariants({ accordianItem: normalizedVariant })}
          >
            <AccordionTrigger
              className={faqVariants({ triggerText: normalizedVariant })}
              classNameTrigger={faqVariants({
                triggerChevron: normalizedVariant,
              })}
            >
              {questionsAndAnswer.fields.question}
            </AccordionTrigger>
            <AccordionContent
              value={questionsAndAnswer.sys.id}
              contentClassName={faqVariants({
                accordianContent: normalizedVariant,
              })}
            >
              {questionsAndAnswer.fields.answer && (
                <RichText
                  content={questionsAndAnswer.fields.answer}
                  pNoSpacing={
                    faqVariants({ pSpacing: normalizedVariant }) === "false"
                  }
                  pClassName={faqVariants({ pText: normalizedVariant })}
                />
              )}
            </AccordionContent>
          </AccordionItem>
        ))}
      </Accordion.Root>
    );
  }

  return (
    <>
      {questionsAndAnswers && questionsAndAnswers.length > 0 && (
        <FaqJsonLd questionsAndAnswers={questionsAndAnswers} />
      )}
      {faq.fields.variant === "title-top" ? (
        <div className="m-auto mb-20 max-w-4xl px-2">
          {faq.fields.title && (
            <H2 className="mb-6 text-center text-5xl">{faq.fields.title}</H2>
          )}
          <FaqAccordian />
        </div>
      ) : (
        <div className="mx-auto grid max-w-screen-lg gap-5 pb-12 sm:px-6 md:grid-cols-2">
          <div className="max-w-md space-y-5 lg:pr-20">
            <H1 className="leading-[1.2]">{faq.fields.title}</H1>
            <p className="text-sm">{faq.fields.description}</p>

            {!faq.fields.displayOptions?.includes("hide-contact-email") && (
              <div className="mt-6 flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm">
                  <EnvelopeClosedIcon className="text-black" />
                </div>
                <Link href="mailto:hello@yardzen.com">hello@yardzen.com</Link>
              </div>
            )}

            {!faq.fields.displayOptions?.includes(
              "hide-contact-phone-number",
            ) && (
              <div className="flex items-center gap-2">
                <div className="flex h-6 w-6 items-center justify-center rounded-full bg-white shadow-sm">
                  <FontAwesomeIcon icon={faPhone} className="text-black" />
                </div>
                <Link href="tel:+18889273936">(888) 927-3936</Link>
              </div>
            )}
          </div>
          <FaqAccordian />
        </div>
      )}
    </>
  );
};

const AccordionItem = React.forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    value: string;
  }
>(({ children, className, ...props }, forwardedRef) => (
  <Accordion.Item
    className={classNames(
      "border-black-elderberry ring-douglas-fir overflow-hidden border-b transition-all focus-within:relative focus-within:z-10",
      className,
    )}
    {...props}
    ref={forwardedRef}
  >
    {children}
  </Accordion.Item>
));
AccordionItem.displayName = "AccordionItem";

const AccordionTrigger = React.forwardRef<
  HTMLButtonElement,
  {
    children: ReactNode;
    className?: string;
    classNameTrigger?: string;
  }
>(({ children, className, classNameTrigger, ...props }, forwardedRef) => (
  <Accordion.Header className="border-black-elderberry flex">
    <Accordion.Trigger
      className={cn(
        "hover:bg-douglas-fir/5 focus:bg-douglas-fir/5 group flex flex-1 cursor-pointer items-center justify-between rounded-l px-5 text-left outline-none transition-all",
        className,
      )}
      {...props}
      ref={forwardedRef}
    >
      <div className="flex-1">{children}</div>
      <ChevronDownIcon
        className={cn(
          "transition-transform duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)] group-data-[state=open]:rotate-180",
          classNameTrigger,
        )}
        aria-hidden
      />
    </Accordion.Trigger>
  </Accordion.Header>
));
AccordionTrigger.displayName = "AccordionTrigger";

const AccordionContent = React.forwardRef<
  HTMLDivElement,
  {
    children: ReactNode;
    className?: string;
    contentClassName?: string;
    value: string;
  }
>(({ children, className, contentClassName, ...props }, forwardedRef) => (
  // forceMount keeps closed answers in the server-rendered HTML so search and
  // AI crawlers can read them; the grid-rows transition handles the collapse
  // since Radix no longer unmounts (or hides) closed content for us.
  <Accordion.Content
    forceMount
    className={cn(
      "grid text-[15px] transition-[grid-template-rows,visibility] duration-300 ease-[cubic-bezier(0.87,_0,_0.13,_1)]",
      "data-[state=closed]:invisible data-[state=closed]:grid-rows-[0fr] data-[state=open]:grid-rows-[1fr]",
      className,
    )}
    {...props}
    ref={forwardedRef}
  >
    <div className="min-h-0 overflow-hidden">
      <div className={contentClassName}>{children}</div>
    </div>
  </Accordion.Content>
));
AccordionContent.displayName = "AccordionContent";
